import { FontAwesomeIcon } from 'module/ui/components/atoms/icon/icon';
import { faChevronDown, faSparkles } from '@fortawesome/pro-regular-svg-icons';
import { useEmailSummaryQuery } from 'queries/email-messages';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

/** Fetches the summary for an email thread. Shared by the emails-v2 page and the email-thread drawer. */
export const useEmailSummary = (emailThreadId: number | undefined) => {
  const { data, isLoading: isSummaryLoading } =
    useEmailSummaryQuery(emailThreadId);
  const summaryPoints = data ?? [];
  const hasSummary = summaryPoints.length > 0;

  return { summaryPoints, isSummaryLoading, hasSummary };
};

const SummaryLabel = styled.span`
  font-size: var(--font-size-base);
  font-weight: 600;
  color: var(--text-primary);
  white-space: nowrap;
`;

const StyledSummaryCard = styled.button<{
  $expanded: boolean;
  $collapsedWidth: string;
  $expandedMaxWidth?: string;
}>`
  display: flex;
  flex-direction: column;
  /* Width stays constant — the collapse/expand effect is driven entirely by
     an animated max-width (matching the original inline SummaryCard); if
     width itself also changed between states, it would snap instantly since
     only max-width transitions below. */
  width: 100%;
  max-width: ${({ $expanded, $collapsedWidth, $expandedMaxWidth }) =>
    $expanded ? ($expandedMaxWidth ?? 'none') : $collapsedWidth};
  border-radius: var(--Radius-8, 8px);
  background: var(--Default-Blue-10, #edf5ff);
  border: none;
  cursor: pointer;
  text-align: left;
  overflow: hidden;
  transition:
    max-width 250ms ease-in-out,
    background 150ms ease;

  &:hover {
    background: var(--blue-20, #daeaff);
  }
`;

const SummaryCardHeader = styled.div`
  display: flex;
  height: 42px;
  align-items: center;
  justify-content: space-between;
  padding: 0 var(--spacing-05);
  flex-shrink: 0;
`;

const SummaryCardHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-03);
`;

const SummaryChevron = styled.span<{ $expanded: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: var(--icon-secondary);
  transition: transform 250ms ease-in-out;
  transform: rotate(${({ $expanded }) => ($expanded ? '0deg' : '-90deg')});
`;

const SummaryCardBody = styled.div<{ $expanded: boolean }>`
  display: grid;
  grid-template-rows: ${({ $expanded }) => ($expanded ? '1fr' : '0fr')};
  transition: grid-template-rows 250ms ease-in-out;
`;

/* Must have overflow:hidden and NO padding so grid-template-rows:0fr collapses to true zero. */
const SummaryCardBodyInner = styled.div`
  overflow: hidden;
  min-height: 0;
`;

const SummaryCardBodyContent = styled.div`
  padding: 0 var(--spacing-05) 12px;
`;

const SummaryBulletList = styled.ul`
  margin: 0;
  padding-left: var(--spacing-05);
  display: flex;
  flex-direction: column;
  gap: var(--spacing-02);
`;

const SummaryBulletItem = styled.li`
  font-size: var(--font-size-base);
  color: var(--text-primary);
  line-height: var(--line-height-05);
`;

export interface EmailSummaryCardProps {
  summaryPoints: string[];
  /** Width while collapsed — a small chip by default, matching the emails-v2 page. */
  collapsedWidth?: string;
  /** Max-width cap once expanded. */
  expandedMaxWidth?: string;
  'data-cy'?: string;
}

/** Collapsible AI summary card. Shared by the emails-v2 page and the email-thread drawer so both stay visually and behaviorally in sync. */
export const EmailSummaryCard = ({
  summaryPoints,
  collapsedWidth = '220px',
  expandedMaxWidth = '800px',
  'data-cy': dataCy,
}: EmailSummaryCardProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (summaryPoints.length === 0) {
    return null;
  }

  return (
    <StyledSummaryCard
      type="button"
      data-cy={dataCy}
      $expanded={isExpanded}
      $collapsedWidth={collapsedWidth}
      $expandedMaxWidth={expandedMaxWidth}
      onClick={() => setIsExpanded((v) => !v)}
    >
      <SummaryCardHeader>
        <SummaryCardHeaderLeft>
          <FontAwesomeIcon icon={faSparkles} size={14} />
          <SummaryLabel>
            <FormattedMessage id="summary" defaultMessage="Summary" />
          </SummaryLabel>
        </SummaryCardHeaderLeft>
        <SummaryChevron $expanded={isExpanded}>
          <FontAwesomeIcon icon={faChevronDown} size={12} />
        </SummaryChevron>
      </SummaryCardHeader>
      <SummaryCardBody $expanded={isExpanded}>
        <SummaryCardBodyInner>
          <SummaryCardBodyContent>
            <SummaryBulletList>
              {summaryPoints.map((point, i) => (
                <SummaryBulletItem key={i}>{point}</SummaryBulletItem>
              ))}
            </SummaryBulletList>
          </SummaryCardBodyContent>
        </SummaryCardBodyInner>
      </SummaryCardBody>
    </StyledSummaryCard>
  );
};
